import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Plus,
  Trash2,
  AlertCircle,
  ArrowLeft,
  Save,
  Leaf,
  Drumstick,
  UtensilsCrossed,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_all_menu,
  save_update_menu,
  get_view_menu,
  APL_LINK,
  save_update_category,
  save_variation,
} from "../ServiceConnection/serviceconnection";
import Loader from "../components/layout/Loader";

// Debounce hook
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function AddEditMenuItem() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const primary_id = location.state?.id;

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menuOptions, setMenuOptions] = useState([]);
  const [addonOptions, setAddonOptions] = useState([]);
  const [addons2, setAddons2] = useState([]);
  const [variationOptions, setVariationOptions] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    categoryId: "",
    menuName: "",
    internalName: "",
    itemCode: "",
    originalPrice: "",
    discountPercentage: "0",
    discountedPrice: "",
    description: "",
    nutrition: "",
    vegNonVeg: "",
    mealBeverage: "",
    isFavorite: false,
    removeFromQR: false,
    isCombo: false,
  });

  const [orderTypes, setOrderTypes] = useState([]);
  const [exposeOptions, setExposeOptions] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [variations, setVariations] = useState([]);
  const [combos, setCombos] = useState([]);
  const [isAddonChecked, setIsAddonChecked] = useState(false);
  const [isComboUsingPrice, setIsComboUsingPrice] = useState(false);
  const [errors, setErrors] = useState({});

  // Dialog states
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isVariationDialogOpen, setIsVariationDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryCuisine, setNewCategoryCuisine] = useState("");
  const [newCategoryPreparingTime, setNewCategoryPreparingTime] = useState("");
  const [newVariationInternal, setNewVariationInternal] = useState("");
  const [newVariationExternal, setNewVariationExternal] = useState("");
  const [newVariationType, setNewVariationType] = useState("");

  // Debounced name for auto-generating code
  const debouncedName = useDebounce(formData.menuName, 500);

  // Generate item code
  const generateItemCode = useCallback((existingItems = []) => {
    if (!existingItems || existingItems.length === 0) {
      return "ITM-1";
    }
    const maxId = Math.max(
      ...existingItems.map((item) => parseInt(item.primary_id) || 0),
    );
    return `ITM-${maxId + 1}`;
  }, []);

  // Calculate discounted price
  const calculateDiscountedPrice = useCallback((price, discount) => {
    const validPrice = parseFloat(price) || 0;
    const validDiscount = parseFloat(discount) || 0;
    return (validPrice - (validPrice * validDiscount) / 100).toFixed(2);
  }, []);

  // Handle field change
  const handleFieldChange = useCallback(
    (field, value) => {
      setFormData((prev) => {
        const updated = { ...prev, [field]: value };

        // Auto-calculate discounted price
        if (field === "originalPrice" || field === "discountPercentage") {
          updated.discountedPrice = calculateDiscountedPrice(
            field === "originalPrice" ? value : updated.originalPrice,
            field === "discountPercentage" ? value : updated.discountPercentage,
          );
        }

        return updated;
      });

      // Clear error for this field
      setErrors((prev) => {
        const { [field]: removed, ...rest } = prev;
        return rest;
      });
    },
    [calculateDiscountedPrice],
  );

  // Handle checkbox arrays (order types, expose options)
  const handleCheckboxArray = useCallback((array, setArray, value) => {
    setArray((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  }, []);

  // Handle addon selection
  const handleAddonToggle = useCallback((addonName, addonId) => {
    setSelectedAddons((prev) =>
      prev.includes(addonName)
        ? prev.filter((item) => item !== addonName)
        : [...prev, addonName],
    );
    setSelectedAddonIds((prev) =>
      prev.includes(addonId)
        ? prev.filter((id) => id !== addonId)
        : [...prev, addonId],
    );
  }, []);

  // Variations management
  const addVariation = useCallback(() => {
    setVariations((prev) => [
      ...prev,
      {
        id: Date.now(),
        price: 0,
        selectedAddons: "",
        sid: "",
      },
    ]);
  }, []);

  const updateVariation = useCallback((index, field, value, sid = "") => {
    setVariations((prev) => {
      const updated = [...prev];
      if (field === "selectedAddons") {
        updated[index].selectedAddons = value;
        updated[index].sid = sid;
      } else {
        updated[index][field] = value;
      }
      return updated;
    });
  }, []);

  const removeVariation = useCallback((index) => {
    setVariations((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Combo management
  const addCombo = useCallback(() => {
    setCombos((prev) => [
      ...prev,
      {
        id: Date.now(),
        quantity: "",
        price: "",
        totalPrice: 0,
        selectedItem: "",
        itemId: "",
      },
    ]);
  }, []);

  const updateCombo = useCallback(
    (id, field, value) => {
      setCombos((prev) =>
        prev.map((combo) => {
          if (combo.id === id) {
            const updated = { ...combo, [field]: value };
            if (field === "quantity" || field === "price") {
              updated.totalPrice = isComboUsingPrice
                ? parseFloat(updated.price) || 0
                : (parseFloat(updated.quantity) || 0) *
                  (parseFloat(updated.price) || 0);
            }
            return updated;
          }
          return combo;
        }),
      );
    },
    [isComboUsingPrice],
  );

  const removeCombo = useCallback((id) => {
    setCombos((prev) => prev.filter((combo) => combo.id !== id));
  }, []);

  // Fetch master data
  const fetchMasterData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);

    try {
      const response = await server_post_data(get_all_menu, fd);

      if (!response.data.error) {
        setCategories(response.data.message.categories || []);
        setMenuOptions(response.data.message.items || []);
        setAddonOptions(response.data.message.addon || []);
        setVariationOptions(response.data.message.Variation || []);

        const formattedAddons = (response.data.message.addon || []).map(
          (item) => ({
            value: item.primary_id,
            label: item.addon_name,
          }),
        );
        setAddons2(formattedAddons);
      }
    } catch (error) {
      console.error("Error fetching master data:", error);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  // Fetch item data for edit
  const fetchItemData = useCallback(async (id) => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 2);
    fd.append("call_id", id);

    try {
      const response = await server_post_data(get_view_menu, fd);

      if (!response.data.error) {
        const data = response.data.message.data[0];

        setFormData({
          categoryId: data.cate_id || "",
          menuName: data.item_name || "",
          internalName: data.item_external_name || "",
          itemCode: data.item_code || "",
          originalPrice: data.item_original_price || "",
          discountPercentage: data.item_discount || "0",
          discountedPrice: data.item_discounted_price || "",
          description: data.item_description || "",
          nutrition: "",
          vegNonVeg: data.item_class || "",
          mealBeverage: data.meals_beverage_status || "",
          isFavorite: data.favorite_status === 1,
          removeFromQR: data.dineir_qr_status === 1,
          isCombo: data.combo_status === "1",
        });

        // Set order types
        if (data.item_type_status) {
          setOrderTypes(data.item_type_status.split("~@~"));
        }

        // Set expose options
        if (data.expose_item_on_status) {
          setExposeOptions(
            data.expose_item_on_status
              .split("~@~")
              .filter((item) => item.trim() !== ""),
          );
        }

        // Set addons
        if (data.addon_data_status) {
          setSelectedAddons(
            data.addon_data_status
              .split("~@~")
              .filter((item) => item.trim() !== ""),
          );
        }
        if (data.addon_data_ids) {
          setSelectedAddonIds(
            data.addon_data_ids
              .split(",")
              .map((id) => parseInt(id.trim()))
              .filter((id) => !isNaN(id)),
          );
        }

        // Set variations/addons based on type
        setIsAddonChecked(data.variation_addon_status === "1");
        setIsComboUsingPrice(data.combo_using_price === "1");

        // Set combos if applicable
        if (data.combo_status === "1" && response.data.message.combomenulist) {
          const comboData = response.data.message.combomenulist.map(
            (combo) => ({
              id: combo.primary_id,
              quantity: combo.quantity,
              price: combo.price,
              totalPrice: combo.total_price,
              selectedItem: combo.item_name,
              itemId: combo.item_id,
            }),
          );
          setCombos(comboData);
        }

        // Set variations
        if (response.data.message.variationlist) {
          const variationData = response.data.message.variationlist.map(
            (v) => ({
              id: v.primary_id,
              price: v.variation_price,
              selectedAddons: v.variation_name,
              sid: v.variation_id,
            }),
          );
          setVariations(variationData);
        }
      }
    } catch (error) {
      console.error("Error fetching item data:", error);
    } finally {
      setShowLoaderAdmin(false);
    }
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.menuName.trim()) {
      newErrors.menuName = "Item name is required";
    }

    if (!formData.originalPrice || isNaN(formData.originalPrice)) {
      newErrors.originalPrice = "Valid original price is required";
    }

    if (!formData.categoryId) {
      newErrors.categoryId = "Category is required";
    }

    if (!formData.vegNonVeg) {
      newErrors.vegNonVeg = "Please select Veg or Non-Veg";
    }

    if (!formData.mealBeverage) {
      newErrors.mealBeverage = "Please select Meal or Beverage";
    }

    if (orderTypes.length === 0) {
      newErrors.orderTypes = "Select at least one order type";
    }

    if (exposeOptions.length === 0) {
      newErrors.exposeOptions = "Select at least one platform";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, orderTypes, exposeOptions]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields correctly",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();

    if (primary_id) fd.append("menu_id", primary_id);
    fd.append("cat_id", formData.categoryId);
    fd.append(
      "cat_name",
      categories.find((c) => c.primary_id === formData.categoryId)
        ?.item_category || "",
    );
    fd.append("item_name", formData.menuName);
    fd.append("item_original_price", formData.originalPrice);
    fd.append("item_discount", formData.discountPercentage);
    fd.append("item_discounted_price", formData.discountedPrice);
    fd.append("item_class", formData.vegNonVeg);
    fd.append("item_description", formData.description);
    fd.append("item_external_name", formData.internalName);
    fd.append("item_code", formData.itemCode);
    fd.append("favorite_status", formData.isFavorite ? 1 : 0);
    fd.append("meals_beverage_status", formData.mealBeverage);
    fd.append("item_type_status", orderTypes.join("~@~"));
    fd.append("meat_type_tag", orderTypes.join("~@~"));
    fd.append("expose_item_on_status", exposeOptions.join("~@~"));
    fd.append("dineir_qr_status", formData.removeFromQR ? 1 : 0);
    fd.append("combo_status", formData.isCombo ? 1 : 0);
    fd.append("addon_data_status", selectedAddons.join("~@~"));
    fd.append("variation_addon_status", isAddonChecked ? 1 : 0);
    fd.append("combo_using_price", isComboUsingPrice ? 1 : 0);
    fd.append("addon_ids", selectedAddonIds.join(","));
    fd.append("combo_data", JSON.stringify(combos));
    fd.append("item_image_old", "");
    fd.append("testi_image_new", "");

    // Variations
    const formattedVariations = variations
      .filter((v) => v.selectedAddons && v.selectedAddons.trim() !== "")
      .map((v) => ({
        sid: v.sid || v.selectedAddons,
        selectedAddons: v.selectedAddons,
        price: v.price || "0",
      }));

    fd.append("variations", JSON.stringify(formattedVariations));
    fd.append("variation_data", "[]");

    try {
      const response = await server_post_data(save_update_menu, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save item",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: primary_id
            ? "Item updated successfully"
            : "Item created successfully",
        });
        navigate("/items");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    validateForm,
    primary_id,
    formData,
    categories,
    orderTypes,
    exposeOptions,
    selectedAddons,
    selectedAddonIds,
    combos,
    variations,
    isAddonChecked,
    isComboUsingPrice,
    toast,
    navigate,
  ]);

  // Handle new category
  const handleSaveNewCategory = useCallback(async () => {
    if (!newCategoryName.trim() || !newCategoryCuisine.trim() || !newCategoryPreparingTime.trim()) {
      toast({
        title: "Validation Error",
        description: "Please fill all fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("cate_id", 0);
    fd.append("item_category", newCategoryName);
    fd.append("item_cuisine", newCategoryCuisine);
    fd.append("max_time_prepare", newCategoryPreparingTime);
    fd.append("show_hide", 1);
    fd.append("testi_image_new", "");

    try {
      const response = await server_post_data(save_update_category, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Category created successfully",
        });
        fetchMasterData();
        setIsCategoryDialogOpen(false);
        setNewCategoryName("");
        setNewCategoryCuisine("");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [newCategoryName, newCategoryCuisine, toast, fetchMasterData]);

  // Initial load
  useEffect(() => {
    fetchMasterData();
    if (primary_id) {
      fetchItemData(primary_id);
    }
  }, [fetchMasterData, fetchItemData, primary_id]);

  // Auto-generate item code
  useEffect(() => {
    if (!primary_id && menuOptions.length > 0 && !formData.itemCode) {
      const newCode = generateItemCode(menuOptions);
      handleFieldChange("itemCode", newCode);
    }
  }, [
    menuOptions,
    primary_id,
    formData.itemCode,
    generateItemCode,
    handleFieldChange,
  ]);

  return (
    <MainLayout>
      <Header
        title={primary_id ? "Edit Menu Item" : "Add New Menu Item"}
        subtitle={
          primary_id
            ? "Update menu item details"
            : "Create a new item for your menu"
        }
      />

      <div className="p-6 space-y-6">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate("/items")}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Items
        </Button>

        {/* Configuration Section */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Configurations
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Veg/Non-Veg */}
            <div className="space-y-2">
              <Label>
                Food Type <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="veg"
                    checked={formData.vegNonVeg === "veg"}
                    onCheckedChange={() =>
                      handleFieldChange("vegNonVeg", "veg")
                    }
                  />
                  <Label
                    htmlFor="veg"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Leaf className="w-4 h-4 text-success" />
                    Veg
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="non-veg"
                    checked={formData.vegNonVeg === "non-veg"}
                    onCheckedChange={() =>
                      handleFieldChange("vegNonVeg", "non-veg")
                    }
                  />
                  <Label
                    htmlFor="non-veg"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Drumstick className="w-4 h-4 text-destructive" />
                    Non-Veg
                  </Label>
                </div>
              </div>
              {errors.vegNonVeg && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.vegNonVeg}
                </p>
              )}
            </div>

            {/* Meal/Beverage */}
            <div className="space-y-2">
              <Label>
                Type <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="meal"
                    checked={formData.mealBeverage === "1"}
                    onCheckedChange={() =>
                      handleFieldChange("mealBeverage", "1")
                    }
                  />
                  <Label htmlFor="meal" className="cursor-pointer">
                    Meals
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="beverage"
                    checked={formData.mealBeverage === "2"}
                    onCheckedChange={() =>
                      handleFieldChange("mealBeverage", "2")
                    }
                  />
                  <Label htmlFor="beverage" className="cursor-pointer">
                    Beverage
                  </Label>
                </div>
              </div>
              {errors.mealBeverage && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.mealBeverage}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Basic Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Category */}
            <div className="space-y-2">
              <Label>
                Category <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-2">
                <Select
                  value={formData.categoryId}
                  onValueChange={(value) =>
                    handleFieldChange("categoryId", value)
                  }
                >
                  <SelectTrigger
                    className={errors.categoryId ? "border-destructive" : ""}
                  >
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem
                        key={cat.primary_id}
                        value={cat.primary_id.toString()}
                      >
                        {cat.item_category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsCategoryDialogOpen(true)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {errors.categoryId && (
                <p className="text-xs text-destructive">{errors.categoryId}</p>
              )}
            </div>

            {/* Internal Name */}
            <div className="space-y-2">
              <Label>
                Internal Name <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="e.g., Margherita Pizza"
                value={formData.menuName}
                onChange={(e) => handleFieldChange("menuName", e.target.value)}
                className={errors.menuName ? "border-destructive" : ""}
              />
              {errors.menuName && (
                <p className="text-xs text-destructive">{errors.menuName}</p>
              )}
            </div>

            {/* Item Code */}
            <div className="space-y-2">
              <Label>Item Code</Label>
              <Input
                placeholder="Auto-generated"
                value={formData.itemCode}
                readOnly
                className="bg-muted"
              />
            </div>

            {/* External Name */}
            <div className="space-y-2">
              <Label>External Name</Label>
              <Input
                placeholder="Display name"
                value={formData.internalName}
                onChange={(e) =>
                  handleFieldChange("internalName", e.target.value)
                }
              />
            </div>

            {/* Original Price */}
            <div className="space-y-2">
              <Label>
                Original Price <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.originalPrice}
                onChange={(e) =>
                  handleFieldChange("originalPrice", e.target.value)
                }
                className={errors.originalPrice ? "border-destructive" : ""}
              />
              {errors.originalPrice && (
                <p className="text-xs text-destructive">
                  {errors.originalPrice}
                </p>
              )}
            </div>

            {/* Discount Percentage */}
            <div className="space-y-2">
              <Label>Discount %</Label>
              <Input
                type="number"
                placeholder="0"
                value={formData.discountPercentage}
                onChange={(e) =>
                  handleFieldChange("discountPercentage", e.target.value)
                }
              />
            </div>

            {/* Discounted Price */}
            <div className="space-y-2">
              <Label>Discounted Price</Label>
              <Input
                placeholder="Auto-calculated"
                value={formData.discountedPrice}
                readOnly
                className="bg-muted"
              />
            </div>
          </div>

          {/* Order Type */}
          <div className="mt-4 space-y-2">
            <Label>
              Order Type <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap gap-4">
              {["Delivery", "Pick Up", "Dine"].map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={type}
                    checked={orderTypes.includes(type)}
                    onCheckedChange={() =>
                      handleCheckboxArray(orderTypes, setOrderTypes, type)
                    }
                  />
                  <Label htmlFor={type} className="cursor-pointer">
                    {type}
                  </Label>
                </div>
              ))}
            </div>
            {errors.orderTypes && (
              <p className="text-xs text-destructive">{errors.orderTypes}</p>
            )}
          </div>

          {/* Expose Options */}
          <div className="mt-4 space-y-2">
            <Label>
              Expose this Item on <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap gap-4">
              {["Online Orders", "Captain App", "Kiosk", "Other"].map(
                (option) => (
                  <div key={option} className="flex items-center space-x-2">
                    <Checkbox
                      id={option}
                      checked={exposeOptions.includes(option)}
                      onCheckedChange={() =>
                        handleCheckboxArray(
                          exposeOptions,
                          setExposeOptions,
                          option,
                        )
                      }
                    />
                    <Label htmlFor={option} className="cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ),
              )}
            </div>
            {errors.exposeOptions && (
              <p className="text-xs text-destructive">{errors.exposeOptions}</p>
            )}
          </div>

          {/* Description */}
          <div className="mt-4 space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Item description"
              value={formData.description}
              onChange={(e) => handleFieldChange("description", e.target.value)}
              rows={3}
            />
          </div>
        </div>

        {/* General Settings */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            General Settings
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label htmlFor="favorite">Set As Favorite</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  For offline PoS billing only
                </p>
              </div>
              <Checkbox
                id="favorite"
                checked={formData.isFavorite}
                onCheckedChange={(checked) =>
                  handleFieldChange("isFavorite", checked)
                }
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label htmlFor="removeQR">Remove From Dine-in QR</Label>
              </div>
              <Checkbox
                id="removeQR"
                checked={formData.removeFromQR}
                onCheckedChange={(checked) =>
                  handleFieldChange("removeFromQR", checked)
                }
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label htmlFor="combo">Set As Combo</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Create items combo for billing
                </p>
              </div>
              <Checkbox
                id="combo"
                checked={formData.isCombo}
                onCheckedChange={(checked) => {
                  handleFieldChange("isCombo", checked);
                  if (!checked) {
                    setCombos([]);
                    setVariations([]);
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Combo Section (if enabled) */}
        {formData.isCombo && (
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Combo Items
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="comboPrice"
                    checked={isComboUsingPrice}
                    onCheckedChange={setIsComboUsingPrice}
                  />
                  <Label htmlFor="comboPrice" className="cursor-pointer">
                    Combo using price
                  </Label>
                </div>
                <Button onClick={addCombo} variant="outline" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Combo
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {combos.map((combo, index) => (
                <div
                  key={combo.id}
                  className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg bg-muted/30"
                >
                  <div className="space-y-2">
                    <Label>Item</Label>
                    <Select
                      value={combo.selectedItem}
                      onValueChange={(value) =>
                        updateCombo(combo.id, "selectedItem", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Item" />
                      </SelectTrigger>
                      <SelectContent>
                        {menuOptions.map((item) => (
                          <SelectItem
                            key={item.primary_id}
                            value={item.item_name}
                          >
                            {item.item_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      value={combo.quantity}
                      onChange={(e) =>
                        updateCombo(combo.id, "quantity", e.target.value)
                      }
                      readOnly={isComboUsingPrice}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Price</Label>
                    <Input
                      type="number"
                      value={combo.price}
                      onChange={(e) =>
                        updateCombo(combo.id, "price", e.target.value)
                      }
                      readOnly={!isComboUsingPrice}
                    />
                    <p className="text-xs text-muted-foreground">
                      Total: ₹{combo.totalPrice}
                    </p>
                  </div>

                  <div className="flex items-end">
                    {index !== 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCombo(combo.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Addons Section */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Addons</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {addonOptions.map((addon) => (
              <div
                key={addon.primary_id}
                className="flex items-center space-x-2"
              >
                <Checkbox
                  id={`addon-${addon.primary_id}`}
                  checked={selectedAddonIds.includes(addon.primary_id)}
                  onCheckedChange={() =>
                    handleAddonToggle(addon.addon_name, addon.primary_id)
                  }
                />
                <Label
                  htmlFor={`addon-${addon.primary_id}`}
                  className="cursor-pointer"
                >
                  {addon.addon_name}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Variations Section */}
        {!formData.isCombo && (
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Variations
              </h3>
              <Button
                onClick={addVariation}
                variant="outline"
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Variation
              </Button>
            </div>

            <div className="space-y-4">
              {variations.map((variation, index) => (
                <div
                  key={variation.id}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg bg-muted/30"
                >
                  <div className="space-y-2">
                    <Label>Variation</Label>
                    <div className="flex gap-2">
                      <Select
                        value={variation.selectedAddons}
                        onValueChange={(value) => {
                          const selected = variationOptions.find(
                            (v) => v.variation_name === value,
                          );
                          updateVariation(
                            index,
                            "selectedAddons",
                            value,
                            selected?.primary_id || "",
                          );
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Variation" />
                        </SelectTrigger>
                        <SelectContent>
                          {variationOptions.map((v) => (
                            <SelectItem
                              key={v.primary_id}
                              value={v.variation_name}
                            >
                              {v.variation_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setIsVariationDialogOpen(true)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Price</Label>
                    <Input
                      type="number"
                      value={variation.price}
                      onChange={(e) =>
                        updateVariation(index, "price", e.target.value)
                      }
                    />
                  </div>

                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeVariation(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/menu-items")}
            disabled={showLoaderAdmin}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={showLoaderAdmin}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            {showLoaderAdmin ? (
              "Saving..."
            ) : (
              <>
                <Save className="w-4 h-4" />
                {primary_id ? "Update Item" : "Create Item"}
              </>
            )}
          </Button>
        </div>

        {/* Add Category Dialog */}
        <Dialog
          open={isCategoryDialogOpen}
          onOpenChange={setIsCategoryDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Category</DialogTitle>
              <DialogDescription>
                Create a new category for your menu items
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category Name *</Label>
                <Input
                  placeholder="e.g., Appetizers"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Cuisine *</Label>
                <Input
                  placeholder="e.g., Italian"
                  value={newCategoryCuisine}
                  onChange={(e) => setNewCategoryCuisine(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Preparing Time (in minutes) *</Label>
                <Input
                  type="number"
                  placeholder="e.g., 15"
                  value={newCategoryPreparingTime}
                  onChange={(e) => setNewCategoryPreparingTime(e.target.value)}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCategoryDialogOpen(false);
                  setNewCategoryName("");
                  setNewCategoryCuisine("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveNewCategory}
                disabled={
                  !newCategoryName.trim() ||
                  !newCategoryCuisine.trim() ||
                  !newCategoryPreparingTime.trim()
                }
                className="gradient-primary text-primary-foreground"
              >
                Save Category
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Variation Dialog */}
        <Dialog
          open={isVariationDialogOpen}
          onOpenChange={setIsVariationDialogOpen}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Variation</DialogTitle>
              <DialogDescription>
                Create a new variation option
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Internal Name *</Label>
                <Input
                  placeholder="e.g., Small"
                  value={newVariationInternal}
                  onChange={(e) => setNewVariationInternal(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>External Name *</Label>
                <Input
                  placeholder="e.g., S"
                  value={newVariationExternal}
                  onChange={(e) => setNewVariationExternal(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={newVariationType}
                  onValueChange={setNewVariationType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem
                        key={cat.primary_id}
                        value={cat.item_category}
                      >
                        {cat.item_category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsVariationDialogOpen(false);
                  setNewVariationInternal("");
                  setNewVariationExternal("");
                  setNewVariationType("");
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={
                  !newVariationInternal.trim() ||
                  !newVariationExternal.trim() ||
                  !newVariationType
                }
                className="gradient-primary text-primary-foreground"
              >
                Save Variation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}
